/**
 * The projects the owner socket hears besides the viewed one (Part 1,
 * decision 5): Home, where Denker's conversation lives, the project of the
 * conversation picked in the input bar, the projects of cards Denker
 * presented from another project while their own window or Focus shows them
 * (so their updates reach it), and on the desktop the projects of pinned
 * frames. The server sends frame, cursor, question and resource events only
 * to a project's group, so each is subscribed; what they add is
 * routed by `routeDesktopPinEvent` and never reaches the viewed wall. A
 * switch of view keeps what Denker's projects (Home, the target's) added.
 */
import { useEffect, useMemo } from "react";
import { getCurrentDenkerWs } from "@/api/canvas-ws-ref";
import type { Project } from "@/api/spaces";
import { setOwnerExtraProjects } from "@/desktop/pin/desktop-pin-relay";
import {
  useFloatingFrameStore,
  type FloatingFrame,
} from "@/desktop/floating-frame-store";
import { useDesktopPins } from "@/desktop/pin/desktop-pins-store";
import type { FrameData } from "@/lib/frame-data";
import { isTauri } from "@/lib/is-tauri";
import { isOffWallFrameType } from "@/lib/wall-membership";
import {
  useCanvasStore,
  type AwaitingInput,
  type CanvasState,
} from "@/stores/canvas-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useFramesStore, type FramesState } from "@/stores/frames-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";

/** The user's Home project, where Denker's conversation lives. */
export function homeProjectIdOf(state: {
  projects: readonly Project[];
}): string | null {
  return state.projects.find((project) => project.is_home)?.id ?? null;
}

/** Denker's projects now: Home and the picked target's. */
export function denkerProjectIds(): ReadonlySet<string> {
  const ids = [
    homeProjectIdOf(useProjectStore.getState()),
    useInputBarTarget.getState().target?.projectId,
  ];
  return new Set(ids.filter((id): id is string => !!id));
}

/** Whether a project, else a run's project, is one of Denker's. */
function inDenkerProject(): (
  projectId: string | undefined,
  executionId?: string,
) => boolean {
  const projects = denkerProjectIds();
  const { executions } = useCompanionStore.getState();
  return (projectId, executionId) => {
    const owner =
      projectId ?? (executionId && executions.get(executionId)?.spaceId);
    return !!owner && projects.has(owner);
  };
}

/**
 * The frames a switch of view keeps: Denker's projects' off-wall frames, and
 * a card of another project open in its own window (it stays live there).
 */
export function denkerFrameKeep(): (frame: FrameData) => boolean {
  const inDenker = inDenkerProject();
  const windows = useFloatingFrameStore.getState().frames;
  return (frame) =>
    (isOffWallFrameType(frame.frameType) && inDenker(frame.spaceId)) ||
    windows[frame.frameId]?.otherProject === true;
}

/** Projects of other-project cards open in their own windows, as one key. */
function windowProjectsKey(
  windows: Readonly<Record<string, FloatingFrame>>,
  currentProjectId: string | null,
): string {
  const ids = Object.values(windows)
    .filter((w) => w.otherProject && w.projectId !== currentProjectId)
    .map((w) => w.projectId);
  return [...new Set(ids)].sort().join(",");
}

/** Projects of the cards Focus shows from other projects, as one key. */
function focusProjectsKey(
  state: Pick<FramesState, "framesById">,
  focusIds: string,
  currentProjectId: string | null,
): string {
  const ids = new Set<string>();
  for (const id of focusIds ? focusIds.split(",") : []) {
    const frame = state.framesById[id];
    if (
      frame?.spaceId &&
      frame.spaceId !== currentProjectId &&
      !isOffWallFrameType(frame.frameType)
    )
      ids.add(frame.spaceId);
  }
  return [...ids].sort().join(",");
}

/** A switch of view keeps the current run only when it is Denker's. */
export function settleCurrentExecutionOnSwitch(): void {
  const companion = useCompanionStore.getState();
  const current = companion.currentExecutionId;
  if (current && !inDenkerProject()(undefined, current))
    companion.setCurrentExecution(null);
}

/** What the canvas store's `clear` keeps on a switch of view. */
export type CanvasKeptOnSwitch = Pick<
  CanvasState,
  "agentCursors" | "pendingPermissions" | "awaitingInputs"
>;

/** The cursors, permission requests and questions a switch of view keeps. */
export function denkerCanvasKeep(): CanvasKeptOnSwitch {
  const inDenker = inDenkerProject();
  const canvas = useCanvasStore.getState();
  const awaitingInputs = new Map<string, AwaitingInput[]>();
  for (const [frameId, items] of canvas.awaitingInputs) {
    const kept = items.filter((item) =>
      inDenker(item.projectId, item.executionId),
    );
    if (kept.length > 0) awaitingInputs.set(frameId, kept);
  }
  return {
    agentCursors: new Map(
      [...canvas.agentCursors].filter(([, cursor]) =>
        inDenker(cursor.projectId ?? cursor.spaceId),
      ),
    ),
    pendingPermissions: new Map(
      [...canvas.pendingPermissions].filter(([, permission]) =>
        inDenker(undefined, permission.executionId),
      ),
    ),
    awaitingInputs,
  };
}

export interface OwnerExtraProjects {
  /** Projects heard for their desktop pins; their events feed only the widgets. */
  pins: ReadonlySet<string>;
  /** The user's Home project, where Denker's conversation lives. */
  home: string | null;
  /** The project of the conversation picked in the input bar. */
  targetProject: string | null;
  /** Projects of the cards Denker presented from them; only those cards' frame events pass. */
  shown: ReadonlySet<string>;
}

/** Subscribe the owner socket to the extra projects; returns the teardown. */
function subscribeExtraProjects(extra: OwnerExtraProjects): () => void {
  const ws = getCurrentDenkerWs();
  const projectIds = new Set(
    [...extra.pins, ...extra.shown, extra.home, extra.targetProject].filter(
      (projectId): projectId is string => projectId !== null,
    ),
  );
  setOwnerExtraProjects(extra);
  for (const projectId of projectIds) ws?.subscribeProject(projectId);
  return () => {
    setOwnerExtraProjects(null);
    for (const projectId of projectIds) ws?.unsubscribeProject(projectId);
  };
}

/**
 * Called by `useOwnerConnection` after its own project effect. In one commit
 * React runs every effect cleanup before any setup, so the viewed project's
 * unsubscribe and these never undo each other on a view switch.
 */
export function useOwnerExtraProjects(owner: boolean): void {
  const pins = useDesktopPins();
  const currentProjectId = useProjectStore(
    (state) => state.currentProject?.id ?? null,
  );
  const homeId = useProjectStore(homeProjectIdOf);
  const targetProjectId = useInputBarTarget(
    (state) => state.target?.projectId ?? null,
  );
  const windowProjects = useFloatingFrameStore((state) =>
    windowProjectsKey(state.frames, currentProjectId),
  );
  const focusIds = usePresentationStore((state) =>
    state.presentation?.variant === "focus"
      ? state.presentation.frameIds.join(",")
      : "",
  );
  const focusProjects = useFramesStore((state) =>
    focusProjectsKey(state, focusIds, currentProjectId),
  );
  // One string so an unchanged set of projects keeps its subscription.
  const key = useMemo(() => {
    const other = (projectId: string | null) =>
      projectId === currentProjectId ? null : projectId;
    const pinProjects = isTauri()
      ? [...new Set(pins.map((pin) => pin.projectId))]
          .filter((projectId) => projectId !== currentProjectId)
          .sort()
      : [];
    const shown = [
      ...new Set(
        [windowProjects, focusProjects].flatMap((ids) =>
          ids ? ids.split(",") : [],
        ),
      ),
    ].sort();
    return JSON.stringify([
      pinProjects,
      other(homeId),
      other(targetProjectId),
      shown,
    ]);
  }, [
    pins,
    currentProjectId,
    homeId,
    targetProjectId,
    windowProjects,
    focusProjects,
  ]);
  useEffect(() => {
    if (!owner) return;
    const [pinProjects, home, targetProject, shown] = JSON.parse(key) as [
      string[],
      string | null,
      string | null,
      string[],
    ];
    if (
      pinProjects.length === 0 &&
      shown.length === 0 &&
      !home &&
      !targetProject
    )
      return;
    return subscribeExtraProjects({
      pins: new Set(pinProjects),
      home,
      targetProject,
      shown: new Set(shown),
    });
  }, [owner, key]);
}
